


























































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import VuePlotly from "@statnett/vue-plotly";

import { mixins } from "vue-class-component";
import QueryEndpointData from "./dataquery-mixin";

import ModeShareBarChart from "./ModeShareBarChart.vue";

@Component({
    components: {
        VuePlotly,
        ModeShareBarChart,
    },
})
export default class Metrics extends mixins(QueryEndpointData) {
    @Prop() histogram!: boolean;
    @Prop() selectedMetricTitle!: string;
    @Prop() selectedMetric!: any;
    @Prop() protected config!: any;
    @Prop() metric_type?:
        | "supply_chain_leg_cat"
        | "total"
        | "hist_supply_chain"
        | "hist_total"
        | null;

    get metricsHistogramUrl() {
        return (
            process.env.VUE_APP_BACKEND_BASE +
            "/supplychainexplorer/metricshistogram"
        );
    }

    mounted() {
        this.loadData(this.metricsHistogramUrl);
    }

    @Watch("updateRequired")
    reloadData(newVal: any, oldVal: any) {
        this.loadData(this.metricsHistogramUrl);
    }

    /** `selectedMetricTitle` with any leading "Total " / "Avg " / "Average " prefix stripped. */
    get displayedMetricTitle(): string {
        if (!this.selectedMetricTitle) return "";
        return this.selectedMetricTitle.replace(/^(Total|Avg|Average)\s+/i, "");
    }

    /** Plain-text description shown as the native browser `title=`
     *  popup when the user hovers over the histogram's title hotspot. */
    get histogramTitleHover(): string {
        const m = this.selectedMetric?.value;
        const descriptions: { [key: string]: string } = {
            od_km:
                "Distribution of trip distance values for the selected commodity " +
                "and filters. Each bar's height is the sum of tonnes for " +
                "trips whose distance falls in that range. The x-axis is " +
                "log-scaled (km); stacks split each bar into the road and rail " +
                "contributions.",
            od_hour:
                "Distribution of trip duration values for the selected commodity " +
                "and filters. Each bar's height is the sum of tonnes for " +
                "trips whose duration falls in that range. The x-axis is " +
                "log-scaled (hours); stacks split each bar into the road " +
                "and rail contributions.",
            cost_per_tonne:
                "Distribution of transport cost per tonne values for the selected " +
                "commodity and filters. Each bar's height is the sum of " +
                "tonnes for trips whose cost falls in that range. The " +
                "x-axis is log-scaled ($); stacks split each bar into the " +
                "road and rail contributions.",
            co2_per_tonne:
                "Distribution of CO2 emission values per tonne moved for the " +
                "selected commodity and filters. Each bar's height is the " +
                "sum of tonnes for trips whose emissions fall in that " +
                "range. The x-axis is log-scaled (kg CO2/tn); stacks split " +
                "each bar into the road and rail contributions.",
        };
        if (m && descriptions[m]) return descriptions[m];
        return (
            "Distribution of the selected metric across trips for the " +
            "current filters. Bar height is the sum of tonnes per bin; " +
            "the x-axis is log-scaled and the stacks split each bar into " +
            "road and rail contributions."
        );
    }

    /** Compact number for tonnes: k / m / b with one decimal (e.g. 2.3m, 45.6k, 456.0). */
    formatCompact(v: number): string {
        const abs = Math.abs(v);
        if (abs >= 1e9) return (v / 1e9).toFixed(1) + "b";
        if (abs >= 1e6) return (v / 1e6).toFixed(1) + "m";
        if (abs >= 1e3) return (v / 1e3).toFixed(1) + "k";
        return v.toFixed(1);
    }

    get plotData() {
        if (!this.selectedMetric || !this.data || this.data.length === 0) {
            return [];
        }
        // Backend now emits one trace per (metric, mode) pair when the
        // `mode` column is in the source DataFrame — see
        // `get_histogram_traces` in
        // `apps/TransitWeb/backend/supplychainexplorer/views.py`. Pick
        // ALL traces matching the selected metric (one per mode) so we
        // can stack them in the chart. Falls back to a single trace
        // when the backend returns the legacy all-modes-combined shape
        // (no `mode` field on traces).
        const matchingTraces = this.data.filter(
            (item: any) => item.name === this.selectedMetric.value,
        );
        if (matchingTraces.length === 0) return [];

        const MODE_COLORS: { [m: string]: string } = {
            road: "#4E79A7",
            rail: "#E15759",
            sea: "#76B7B2",
            air: "#B07AA1",
        };
        const FALLBACK_COLOR = "#8C8C8C";

        var metricUnits: {
            [key: string]: { prefix?: string; suffix?: string };
        } = {
            cost_per_tonne: { prefix: "$" },
            od_km: { suffix: " km" },
            od_hour: { suffix: " h" },
            co2_per_tonne: { suffix: " kg" },
        };
        var unit = metricUnits[this.selectedMetric.value] || {};
        var prefix = unit.prefix || "";
        var suffix = unit.suffix || "";

        return matchingTraces.map((trace: any) => {
            // Discrete metrics (flagged `linear`) already have their actual values; continuous
            // metrics carry log10 bin centres - invert them for the hover value.
            const linnearX = trace.linear
                ? trace.x
                : trace.x.map((v: number) => Math.pow(10, v));
            const rawMode = (trace.mode || "").toString();
            const modeKey = rawMode.toLowerCase();
            const color = rawMode
                ? MODE_COLORS[modeKey] || FALLBACK_COLOR
                : "#42A5F5";
            const modeLabel = rawMode
                ? rawMode.charAt(0).toUpperCase() + rawMode.slice(1)
                : "All";
            const hoverHeader = rawMode ? `<b>${modeLabel}</b><br>` : "";

            // customdata carries [metric value, pre-formatted tonnes] per bar so the hover can
            // show compact tonnes (k/m/b, one decimal). Border crossings are whole numbers, so
            // their value is shown with no decimals; continuous metrics keep one.
            const customdata = linnearX.map((xv: number, i: number) => [
                xv,
                this.formatCompact((trace.y || [])[i] ?? 0),
                this.formatCompact(xv),
            ]);
            const valueFmt = trace.linear
                ? "%{customdata[0]:d}"
                : `${prefix}%{customdata[2]}${suffix}`;

            return {
                ...trace,
                type: "bar",
                marker: { color },
                opacity: 0.85,
                customdata,
                name: modeLabel,
                hovertemplate:
                    `${hoverHeader}` +
                    `Sum of Tonnes: %{customdata[1]} tn<br>` +
                    `${this.displayedMetricTitle}: ${valueFmt}<extra></extra>`,
            };
        });
    }

    get plotLayout() {
        if (!this.selectedMetric || !this.data || this.data.length === 0) {
            return {};
        }
        var trace = this.data.find(
            (item: any) => item.name === this.selectedMetric.value,
        );
        var binCenters = trace?.x || [];

        var tickvals: any[];
        var ticktext: any[];
        if (trace?.linear) {
            // Discrete metric (e.g. border crossings): ticks sit at the actual values on the
            // linear axis - so show them all.
            tickvals = binCenters;
            ticktext = binCenters.map((v: any) => String(v));
        } else {
            // Limit number of ticks to 8
            var maxTicks = 8;
            var step = Math.ceil(binCenters.length / maxTicks);

            // Generate linnear values for x axis (this is to make it easier to read the data, the actual data values are still log10)
            tickvals = binCenters.filter((_: any, i: any) => i % step === 0);
            ticktext = tickvals.map((v: any) => {
                var val = 10 ** v;
                return val < 1
                    ? val.toFixed(1)
                    : Number(val.toFixed(0)).toLocaleString();
            });
        }

        const isModalView = this.metric_type === "hist_total";

        return {
            title: {
                text: isModalView
                    ? `${this.displayedMetricTitle} (Individual Trips)`
                    : this.displayedMetricTitle,
                font: { size: 18 },
                family: "Roboto",
                x: 0.5,
                xanchor: "center",
            },
            margin: {
                t: isModalView ? 40 : 60,
                b: 40,
                r: isModalView ? 60 : 30,
            },
            ...(isModalView ? { width: 780, autosize: false } : {}),
            xaxis: {
                type: "linear",
                title: {
                    text: `${this.displayedMetricTitle} ${
                        this.selectedMetric?.unit ?? ""
                    }`,
                    // `standoff: 5` pulls the title close to the tick
                    // labels (Plotly's default leaves a wider gap).
                    standoff: 5,
                    ...(isModalView ? { font: { size: 14 } } : {}),
                },
                tickvals,
                ticktext,
                ...(isModalView ? { tickfont: { size: 13 } } : {}),
                showspikes: false,
            },
            bargap: 0.05,
            barmode: "stack",
            showlegend: true,
            legend: {
                orientation: "h",
                x: isModalView ? 1.06 : 0.97,
                xanchor: "right",
                y: isModalView ? 1.07 : 1.15,
                yanchor: "middle",
                font: { size: isModalView ? 13 : 11 },
            },
            yaxis: {
                title: {
                    text: "Sum of Tonnes",
                    ...(isModalView ? { font: { size: 14 } } : {}),
                },
                ...(isModalView ? { tickfont: { size: 13 } } : {}),
            },
            // `hovermode: "closest"` shows only the single closest
            // trace's tooltip. The "x" mode that shows Road and Rail
            // together also renders an unsuppressable axis-level
            // indicator that displays log10 values when the cursor
            // isn't aligned with an x-axis tick — chosen "closest" to
            // avoid that small log10 label entirely. Trade-off: user
            // can only see one mode's tooltip at a time, not both
            // simultaneously.
            hovermode: "closest",
            hoverlabel: {
                font: {
                    color: "#FFFFFF",
                },
            },
        };
    }

    closeHistogram() {
        this.$emit("close");
    }

    get internalDialog(): boolean {
        return this.histogram && this.metric_type === "hist_total";
    }

    onDialogChange(val: boolean) {
        if (!val) {
            this.$emit("close");
        }
    }
}
